import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatTime12h, formatDateLong } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";

function localISO(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const Rebook = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [justBooked, setJustBooked] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get(`/rebook/${token}/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(false))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const availableDates = useMemo(
    () => new Set((info?.availability || []).map((s) => s.date)),
    [info]
  );

  const timesForDate = useMemo(() => {
    if (!selectedDate || !info) return [];
    const iso = localISO(selectedDate);
    const found = info.availability.find((s) => s.date === iso);
    return found ? found.times : [];
  }, [info, selectedDate]);

  const submit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Please pick a date and time.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/rebook/${token}`, {
        session_date: localISO(selectedDate),
        session_time: selectedTime,
      });
      setJustBooked(res.data.booking);
      setSelectedDate(null);
      setSelectedTime(null);
      // Refresh info to reflect new usage
      load();
      toast.success("Session booked — see you soon.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not book. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-16 flex items-center justify-center" data-testid="rebook-loading">
        <Loader2 className="animate-spin text-[#B89B63]" size={30} />
      </main>
    );
  }

  if (info === false) {
    return (
      <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-16" data-testid="rebook-invalid">
        <div className="max-w-lg mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl text-[#4F5D4F] mb-6">Link not found</h1>
          <p className="text-[#4F5D4F]/80 leading-relaxed">
            This re-booking link is either invalid or the Partnership hasn't been paid yet. If you just booked and paid, wait a moment for the confirmation to process, then try again.
          </p>
          <Link
            to="/"
            className="inline-block mt-8 border border-[#4F5D4F] text-[#4F5D4F] hover:bg-[#4F5D4F] hover:text-[#F6F1EA] px-8 py-4 text-xs tracking-eyebrow uppercase transition-colors"
          >
            Return home
          </Link>
        </div>
      </main>
    );
  }

  const done = info.sessions_remaining <= 0;

  return (
    <main className="bg-[#F6F1EA] min-h-screen pt-32 pb-24" data-testid="rebook-page">
      <div className="max-w-[1100px] mx-auto px-6 sm:px-12 lg:px-16">
        <div className="mb-12">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-4">
            Your Coaching Partnership
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#4F5D4F] leading-[1.05]">
            Welcome back,<br />
            <em className="italic text-[#B89B63]">{info.client.name}.</em>
          </h1>
          <p className="mt-6 text-lg text-[#4F5D4F]/80 max-w-xl font-light">
            Book your remaining sessions any time — no payment needed. This link is unique to you; save it.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-8">
            {!done && (
              <div className="bg-white p-8 border border-[#D7B2A3]/50" data-testid="rebook-picker">
                <div className="flex items-baseline justify-between mb-5">
                  <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88]">
                    Choose a date & time
                  </p>
                  <p className="text-xs text-[#8C9A88]">Times shown in ET</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => {
                      setSelectedDate(d);
                      setSelectedTime(null);
                    }}
                    disabled={(date) => !availableDates.has(localISO(date))}
                    className="rounded-none p-0"
                  />
                  <div>
                    <p className="font-serif text-2xl text-[#4F5D4F] mb-4">
                      {selectedDate ? formatDateLong(localISO(selectedDate)) : "Select a date"}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {timesForDate.map((t) => {
                        const active = selectedTime === t;
                        return (
                          <button
                            key={t}
                            onClick={() => setSelectedTime(t)}
                            className={`py-3 px-4 text-sm border transition-colors ${
                              active
                                ? "bg-[#4F5D4F] text-[#F6F1EA] border-[#4F5D4F]"
                                : "border-[#D7B2A3] text-[#4F5D4F] hover:border-[#4F5D4F]"
                            }`}
                            data-testid={`rebook-time-${t.replace(":", "")}`}
                          >
                            {formatTime12h(t)}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={submit}
                      disabled={!selectedDate || !selectedTime || submitting}
                      className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#B89B63] text-[#F6F1EA] hover:bg-[#a08754] disabled:opacity-50 disabled:cursor-not-allowed px-6 py-4 text-xs tracking-eyebrow uppercase transition-colors"
                      data-testid="rebook-submit"
                    >
                      {submitting ? (
                        <><Loader2 className="animate-spin" size={14} /> Booking…</>
                      ) : (
                        <>Confirm this session <ArrowRight size={14} /></>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {justBooked && (
              <div className="bg-[#4F5D4F] text-[#F6F1EA] p-6 flex items-center gap-4" data-testid="rebook-success">
                <CheckCircle2 size={22} className="text-[#B89B63] shrink-0" />
                <p className="text-sm">
                  Booked for <strong>{formatDateLong(justBooked.session_date)}</strong> at{" "}
                  <strong>{formatTime12h(justBooked.session_time)} ET</strong>. Calendar invite on the way.
                </p>
              </div>
            )}

            {done && (
              <div className="bg-white p-10 border border-[#D7B2A3]/50 text-center" data-testid="rebook-done">
                <p className="font-serif text-3xl text-[#4F5D4F]">You've booked all 12 sessions.</p>
                <p className="text-[#4F5D4F]/80 mt-3">Beautiful work. Reach out any time.</p>
              </div>
            )}
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-white p-8 border border-[#D7B2A3]/50 sticky top-28">
              <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-4">Partnership progress</p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-serif text-5xl text-[#4F5D4F]" data-testid="rebook-used">{info.sessions_used}</span>
                <span className="text-[#8C9A88]">/ {info.sessions_total} sessions</span>
              </div>
              <div className="h-1 bg-[#D7B2A3]/30 mb-6">
                <div
                  className="h-full bg-[#B89B63] transition-all"
                  style={{ width: `${(info.sessions_used / info.sessions_total) * 100}%` }}
                />
              </div>

              <p className="text-xs tracking-eyebrow uppercase text-[#8C9A88] mb-3">Your sessions</p>
              <ul className="space-y-2 max-h-80 overflow-y-auto text-sm">
                {info.sessions.map((s, i) => (
                  <li key={`${s.date}-${s.time}`} className="flex justify-between border-b border-[#D7B2A3]/30 pb-2">
                    <span className="text-[#4F5D4F]">
                      {i === 0 && <span className="text-[10px] tracking-eyebrow uppercase text-[#B89B63] mr-2">First</span>}
                      {formatDateLong(s.date)}
                    </span>
                    <span className="text-[#8C9A88]">{formatTime12h(s.time)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default Rebook;
